/**
 * Consent-aware script loader
 *
 * Provides utilities for loading third-party scripts only after the user has
 * granted the appropriate consent category.
 *
 * Usage:
 *   loadScriptWithConsent('analytics', 'https://example.com/script.js');
 *   runWhenConsented('functional', () => setupReferralTracking());
 *
 * To add a new integration:
 *   1. Classify it in INTEGRATION_CATEGORY_MAP (types/cookieConsent.ts)
 *   2. Call loadScriptWithConsent / runWhenConsented with the correct category
 *   3. Listen for 'lookbel:consent-updated' to re-evaluate on preference change
 */

import { ConsentCategory, CONSENT_STORAGE_KEY, COOKIE_CONSENT_VERSION } from '../types/cookieConsent';

/** Read consent synchronously from localStorage (safe for non-React code) */
export function getStoredConsent(): Record<ConsentCategory, boolean> | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== COOKIE_CONSENT_VERSION) return null;
    return {
      necessary: true,
      analytics: parsed?.categories?.analytics ?? false,
      functional: parsed?.categories?.functional ?? false,
      marketing: parsed?.categories?.marketing ?? false,
    };
  } catch {
    return null;
  }
}

/** Returns true if the stored consent grants the given category */
export function hasStoredConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  return getStoredConsent()?.[category] ?? false;
}

/**
 * Dynamically inject a script tag if the user has consented to the given
 * category. If no consent exists yet, registers a one-time listener for the
 * 'lookbel:consent-updated' event and injects the script then.
 */
export function loadScriptWithConsent(
  category: ConsentCategory,
  src: string,
  options: { async?: boolean; defer?: boolean; id?: string } = {}
): void {
  const inject = () => {
    if (document.getElementById(options.id ?? src)) return; // already loaded
    const script = document.createElement('script');
    script.src = src;
    script.async = options.async ?? true;
    if (options.defer) script.defer = true;
    if (options.id) script.id = options.id;
    document.head.appendChild(script);
  };

  if (hasStoredConsent(category)) {
    inject();
    return;
  }

  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (detail?.categories?.[category]) {
      inject();
      window.removeEventListener('lookbel:consent-updated', handler);
    }
  };
  window.addEventListener('lookbel:consent-updated', handler);
}

/**
 * Run an arbitrary callback when the user has (or grants) consent for the
 * given category.  The callback is executed immediately if consent is already
 * stored, or deferred until 'lookbel:consent-updated' fires with the right
 * category enabled.
 */
export function runWhenConsented(
  category: ConsentCategory,
  callback: () => void
): void {
  if (hasStoredConsent(category)) {
    callback();
    return;
  }

  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (detail?.categories?.[category]) {
      callback();
      window.removeEventListener('lookbel:consent-updated', handler);
    }
  };
  window.addEventListener('lookbel:consent-updated', handler);
}
